import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  MenuItem,
  Select,
  Pagination,
  IconButton,
  Checkbox,
  FormControl,
  InputLabel,
  OutlinedInput,
  Stack,
  Chip,
  Grid,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  Menu,
  ListItemIcon,
  ListItemText,
  TextField,
  Snackbar,
  CircularProgress,
  Alert,
  Breadcrumbs,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import DateRangeIcon from "@mui/icons-material/DateRange";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableRowsIcon from "@mui/icons-material/TableRows";
import DescriptionIcon from "@mui/icons-material/Description";
import { DateRangePicker } from "react-date-range";
import moment from "moment";
import axios from "axios";
import { useSelector } from "react-redux";
import { useAuthToken } from "../TokenContext";
import { useIntl } from "react-intl";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { useHistory, Link } from 'react-router-dom';
import './controlplane/ArtemyStyles.css';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  backgroundColor: 'var(--artemy-bg-card)',
  border: '1px solid var(--artemy-border)',
  boxShadow: 'none',
  borderRadius: 14,
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  maxWidth: 1400,
  margin: '0 auto',
}));
const LoaderWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '400px',
  gap: '16px'
});


const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: 'var(--artemy-bg-elevated)',
  color: 'var(--artemy-text-secondary)',
  fontWeight: "bold",
  fontSize: "11px",
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: '1px solid var(--artemy-border)',
  fontFamily: 'var(--artemy-font-mono)',
}));

// Editable Row Component
const EditableTableRow = ({
  activity,
  userStories,
  onSave,
  selectedIds,
  handleCheckboxChange,
  handleViewAndTranscribe,
  token,
  user,
  intl
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({
    jira_issue: activity.jira_issue || "",
    today_focus: activity.today_focus || "",
    accomplishments: activity.accomplishments || "",
    blockers: activity.blockers || "",
    loom_url: activity.loom_url || "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      const poddata = [{
        pod_candidates: activity.firstName,
        today_focus: editedData.today_focus,
        accomplishments: editedData.accomplishments,
        loom_url: editedData.loom_url,
        blockers: editedData.blockers,
        id: activity.id,
        jira_issue: editedData.jira_issue
      }];

      const response = await fetch(
        process.env.REACT_APP_TALENTLEAD_PODACTIVITYUPDATELEAD,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({
            poddata,
            status: 'draft'
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Update failed');
      }

      setSnackbar({
        open: true,
        message: "Successfully updated activity",
        severity: "success"
      });
      setIsEditing(false);
      if (onSave) onSave();
    } catch (error) {
      console.error("Error updating activity:", error);
      setSnackbar({
        open: true,
        message: "Failed to update activity",
        severity: "error"
      });
    }
  };

  const handleCancel = () => {
    setEditedData({
      jira_issue: activity.jira_issue || "",
      today_focus: activity.today_focus || "",
      accomplishments: activity.accomplishments || "",
      blockers: activity.blockers || "",
      loom_url: activity.loom_url || "",
    });
    setIsEditing(false);
  };

  const handleChange = (field) => (event) => {
    setEditedData({
      ...editedData,
      [field]: event.target.value
    });
  };
  const handleVideoUrlChange = (event) => {
    setEditedData({
      ...editedData,
      loom_url: event.target.value
    });
  };
  const { isLoggedIn } = useSelector((state) => state.auth);
  const isUserLoggedIn = isLoggedIn === true;
  const history = useHistory();

  const handleVideoClick = async (loomUrl, id) => {
    try {
      if (!isLoggedIn) {
        sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
        history.push('/login');
        return;
      }

      const [filename] = loomUrl.split('?')[0].split('/').slice(-1);
      const cloudFrontUrl = process.env.REACT_APP_CLOUDFRONTs3;

      // Add authorization header to get signed URL
      const response = await axios.post(
        process.env.REACT_APP_TALENT_CLOUDFRONTAPI,
        { "url": cloudFrontUrl + filename },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const responseBody = JSON.parse(response.data.body);
      const signedUrl = responseBody.signedUrl;

      if (!signedUrl) {
        throw new Error('Signed URL not found');
      }

      // Create encoded state with signed URL
      const state = btoa(JSON.stringify({
        url: signedUrl,
        exp: Date.now() + (5 * 60 * 1000), // 5 minutes expiration
        isAuth: true
      }));

      // Open in secure viewer
      window.open(`/video-viewer/${state}`, '_blank', 'width=1280,height=720');

    } catch (error) {
      console.error('Error handling video:', error);
      alert('Error accessing video. Please try again.');
    }
  };


  return (
    <>
      <tr
        style={{
          borderBottom: '1px solid var(--artemy-border)',
          backgroundColor: isEditing || activity.posted_to_client === 1 ? 'rgba(var(--artemy-accent-rgb), 0.05)' : 'transparent',
          transition: 'background-color 0.2s',
          ...(isEditing ? { backgroundColor: 'var(--artemy-bg-elevated)' } : {})
        }}
      >
        <td style={{ padding: '16px' }}>
          <Checkbox
            color="primary"
            checked={selectedIds.includes(activity.id)}
            onChange={(e) => handleCheckboxChange(e, activity.id)}
            disabled={activity.posted_to_client === 1 || isEditing}
            sx={{ color: 'var(--artemy-text-secondary)' }}
          />
        </td>
        <td style={{ padding: '16px', color: 'var(--artemy-text-primary)' }}>{activity.firstName}</td>
        <td style={{ padding: '16px', color: 'var(--artemy-text-primary)' }}>
          {isEditing ? (
            <Select
              fullWidth
              value={editedData.jira_issue}
              onChange={handleChange('jira_issue')}
              size="small"
              sx={{ color: 'var(--artemy-text-primary)' }}
            >
              {userStories.map((story) => (
                <MenuItem key={story.issue_key} value={story.issue_key}>
                  {story.issue_key} - {story.issue_summary}
                </MenuItem>
              ))}
            </Select>
          ) : (
            activity.jira_issue
          )}
        </td>
        <td style={{ padding: '16px', color: 'var(--artemy-text-primary)' }}>
          {isEditing ? (
            <TextField
              fullWidth
              multiline
              rows={2}
              value={editedData.today_focus}
              onChange={handleChange('today_focus')}
              size="small"
              sx={{ input: { color: 'var(--artemy-text-primary)' } }}
            />
          ) : (
            activity.today_focus
          )}
        </td>
        <td style={{ padding: '16px', color: 'var(--artemy-text-primary)' }}>
          {isEditing ? (
            <TextField
              fullWidth
              multiline
              rows={2}
              value={editedData.accomplishments}
              onChange={handleChange('accomplishments')}
              size="small"
              sx={{ input: { color: 'var(--artemy-text-primary)' } }}
            />
          ) : (
            activity.accomplishments
          )}
        </td>
        <td style={{ padding: '16px', color: 'var(--artemy-text-primary)' }}>
          {isEditing ? (
            <TextField
              fullWidth
              multiline
              rows={2}
              value={editedData.blockers}
              onChange={handleChange('blockers')}
              size="small"
              sx={{ input: { color: 'var(--artemy-text-primary)' } }}
            />
          ) : (
            activity.blockers
          )}
        </td>
        <td style={{ padding: '16px', color: 'var(--artemy-text-primary)' }}>
          {isEditing ? (
            <TextField
              fullWidth
              size="small"
              value={editedData.loom_url || ""}
              onChange={handleVideoUrlChange}
              placeholder="Enter video URL"
              sx={{ minWidth: '200px', input: { color: 'var(--artemy-text-primary)' } }}
            />
          ) : (
            activity.loom_url && isUserLoggedIn ? (
              <Button
                variant="text"
                size="small"
                onClick={() => handleVideoClick(activity.loom_url, activity.id)}
                startIcon={<VideoCallIcon />}
                sx={{ color: 'var(--artemy-accent)' }}
              >
                View Video
              </Button>
            ) : (
              "No video"
            )
          )}
        </td>
        <td style={{ padding: '16px', color: 'var(--artemy-text-secondary)' }}>
          {moment(activity.createdAt).format('MM-DD-YYYY')}
        </td>
        <td style={{ padding: '16px' }}>
          <Chip
            label={activity.posted_to_client === 1
              ? intl.formatMessage({ id: "approved" })
              : intl.formatMessage({ id: "pending" })
            }
            color={activity.posted_to_client === 1 ? "success" : "warning"}
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        </td>
        <td style={{ padding: '16px' }}>
          <Stack direction="row" spacing={1}>
            {!activity.posted_to_client && !isEditing && (
              <IconButton
                color="primary"
                size="small"
                onClick={handleEdit}
                sx={{ color: 'var(--artemy-accent)' }}
              >
                <EditIcon />
              </IconButton>
            )}
            {isEditing && (
              <>
                <IconButton
                  color="success"
                  size="small"
                  onClick={handleSave}
                  sx={{ color: 'var(--artemy-green)' }}
                >
                  <SaveIcon />
                </IconButton>
                <IconButton
                  color="error"
                  size="small"
                  onClick={handleCancel}
                  sx={{ color: 'var(--artemy-red)' }}
                >
                  <CancelIcon />
                </IconButton>
              </>
            )}
          </Stack>
        </td>
      </tr>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
const SortableTableHeader = ({ column, label, sortConfig, onSort }) => (
  <th style={{ backgroundColor: 'var(--artemy-bg-base)', borderBottom: '1px solid var(--artemy-border)', padding: '16px', textAlign: 'left', position: 'sticky', top: 0, zIndex: 1 }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      userSelect: 'none',
      color: 'var(--artemy-text-secondary)',
      fontFamily: 'var(--artemy-font-mono)',
      fontSize: '11px',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    }}
      onClick={() => onSort(column)}>
      {label}
      <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '8px' }}>
        <ArrowUpwardIcon sx={{
          fontSize: '0.8rem',
          color: sortConfig.column === column && sortConfig.direction === 'asc' ? 'var(--artemy-accent)' : 'rgba(255,255,255,0.1)'
        }} />
        <ArrowDownwardIcon sx={{
          fontSize: '0.8rem',
          marginTop: '-8px',
          color: sortConfig.column === column && sortConfig.direction === 'desc' ? 'var(--artemy-accent)' : 'rgba(255,255,255,0.1)'
        }} />
      </div>
    </div>
  </th>
);
function PodActivitystakeholderviewall({ collapsed }) {
  // State declarations
  const [selectedPodId, setSelectedPodId] = useState([]);
  const [editableData, setEditableData] = useState([]);
  const [podDetails, setPodDetails] = useState([]);
  const [podActivities, setPodActivities] = useState([]);
  const [selectedPodDetails, setSelectedPodDetails] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const token = useAuthToken();
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [selectedTeamMember, setSelectedTeamMember] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [userStories, setUserStories] = useState([]);
  const [tempDateRange, setTempDateRange] = useState([
    {
      startDate: moment().subtract(7, "days").toDate(),
      endDate: moment().toDate(),
      key: "selection",
    },
  ]);
  const { isLoggedIn, user } = useSelector((state) => state.auth);
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(true);

  const [dateRange, setDateRange] = useState([
    {
      startDate: moment().subtract(7, "days").toDate(),
      endDate: moment().toDate(),
      key: "selection",
    },
  ]);

  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [statusFilter, setStatusFilter] = useState("all");

  const handleSort = (column) => {
    const newDirection =
      sortConfig.column === column && sortConfig.direction === 'asc'
        ? 'desc'
        : 'asc';

    setSortConfig({ column, direction: newDirection });

    const sortedActivities = [...filteredActivities].sort((a, b) => {
      let compareA, compareB;

      switch (column) {
        case 'status':
          compareA = a.posted_to_client === 1 ? 'Approved' : 'Pending';
          compareB = b.posted_to_client === 1 ? 'Approved' : 'Pending';
          return newDirection === 'asc'
            ? compareA.localeCompare(compareB)
            : compareB.localeCompare(compareA);

        case 'createdAt':
          compareA = new Date(a.createdAt);
          compareB = new Date(b.createdAt);
          return newDirection === 'asc'
            ? compareA - compareB
            : compareB - compareA;

        default:
          return 0;
      }
    });

    setFilteredActivities(sortedActivities);
  };

  // Statistics calculations
  const stats = [
    {
      title: intl.formatMessage({ id: "totalActivities" }),
      value: filteredActivities.length,
      color: "var(--artemy-accent)",
    },
    {
      title: intl.formatMessage({ id: "pendingApproval" }),
      value: filteredActivities.filter(a => !a.posted_to_client).length,
      color: "var(--artemy-yellow)",
    },
    {
      title: intl.formatMessage({ id: "approved" }),
      value: filteredActivities.filter(a => a.posted_to_client === 1).length,
      color: "var(--artemy-green)",
    },
  ];

  // Fetch user stories
  useEffect(() => {
    const fetchUserStories = async () => {
      try {
        const response = await fetch(
          process.env.REACT_APP_CLIENT_JIRAISSUESLISTTALENTS,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              c_id: user.data[0].c_id,
            }),
          }
        );

        const data = await response.json();
        if (data.statusCode === 200 && data.body) {
          const parsedBody = JSON.parse(data.body);
          if (Array.isArray(parsedBody.jiraIssues)) {
            setUserStories(parsedBody.jiraIssues);
          }
        }
      } catch (error) {
        console.error('Error fetching user stories:', error);
      }
    };

    fetchUserStories();
  }, [user.data]);

  // Fetch pod activities
  const fetchData = async () => {
    setIsLoading(true);
    try {



      const response = await fetch(
        process.env.REACT_APP_CLIENT_GETPODACTIVITYSTAKEHOLDER,
        {
          method: "POST",
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            "client_code": user.data[0].client_code,
            "stakeholder_id": user.data[0].stakeholder_id,
            status: "view_all",
          }),
        }
      );

      const data = await response.json();
      setPodDetails(data.data.podDetails);
      const activities = data.data.activities;
      const sortedActivities = activities.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setPodActivities(sortedActivities);
      setFilteredActivities(sortedActivities);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token, user.data]);

  const isActivityInRange = (activity, range) => {
    // If range is null or contains null dates, it means it is not filtered by date
    if (!range || !range.startDate || !range.endDate) {
      return true;
    }

    if (!activity.createdAt) {
      return false;
    }

    const activityDate = new Date(activity.createdAt);
    const startDate = new Date(range.startDate);
    const endDate = new Date(range.endDate);

    activityDate.setHours(0, 0, 0, 0);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    return activityDate >= startDate && activityDate <= endDate;
  };

  useEffect(() => {
    let results = [...podActivities];

    // Filter by Date Range
    if (dateRange && dateRange[0] && dateRange[0].startDate && dateRange[0].endDate) {
      results = results.filter(activity => isActivityInRange(activity, dateRange[0]));
    }

    // Filter by Pods
    if (selectedPodId.length > 0) {
      results = results.filter(activity =>
        selectedPodId.some(podId => podId.toString() === activity.pod_id.toString())
      );
    }

    // Filter by Team Member
    if (selectedTeamMember) {
      results = results.filter(activity => activity.firstName === selectedTeamMember);
    }

    // Filter by Status
    if (statusFilter !== "all") {
      results = results.filter(activity => {
        if (statusFilter === "approved") {
          return activity.posted_to_client === 1;
        } else if (statusFilter === "pending") {
          return activity.posted_to_client === 0;
        }
        return true;
      });
    }

    setFilteredActivities(results);
    setCurrentPage(1);
  }, [podActivities, dateRange, selectedPodId, selectedTeamMember, statusFilter]);

  const handlePodSelect = (event) => {
    const selectedValues = event.target.value;
    setSelectedPodId(selectedValues);

    const lastSelectedPod = podDetails.find(
      (pod) => pod.id === selectedValues[selectedValues.length - 1]
    );
    setSelectedPodDetails(lastSelectedPod || null);
  };

  const handleDateRangeChange = (ranges) => {
    setDateRange([ranges.selection]);
  };

  const handleTeamMemberSelect = (event) => {
    setSelectedTeamMember(event.target.value);
  };

  const handleCheckboxChange = (event, id) => {
    if (event.target.checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
    }
  };

  const handleApprove = async () => {
    if (selectedIds.length === 0) {
      alert(intl.formatMessage({ id: "selectItemsWarning" }));
      return;
    }

    try {
      const response = await fetch(process.env.REACT_APP_CLIENT_PODACTIVITYPOSTLEAD, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          poddata: [{ id: selectedIds, manager_id: user.data[0].emp_id }],
          manager_id: user.data[0].emp_id,
        }),
      });

      if (response.ok) {
        alert(intl.formatMessage({ id: "approvalSuccess" }));
        fetchData();
        setSelectedIds([]);
      } else {
        throw new Error('Approval failed');
      }
    } catch (error) {
      console.error("Error:", error);
      alert(intl.formatMessage({ id: "approvalError" }));
    }
  };

  const handleViewAndTranscribe = async (loomUrl, id) => {
    try {
      const [filename] = loomUrl.split('?')[0].split('/').slice(-1);
      const cloudFrontUrl = process.env.REACT_APP_CLOUDFRONTs3;
      const fullUrl = cloudFrontUrl + filename;

      const response = await axios.post(
        process.env.REACT_APP_TALENT_CLOUDFRONTAPI,
        { "url": fullUrl }
      );

      const responseBody = JSON.parse(response.data.body);
      const signedUrl = responseBody.signedUrl;

      if (!signedUrl) {
        console.error('Signed URL not found:', responseBody);
        alert('Error transcribing video. Signed URL not found.');
        return;
      }

      const updatedData = editableData.map((item) =>
        item.id === id ? { ...item, loom_url: signedUrl } : item
      );
      setEditableData(updatedData);
      window.open(signedUrl, '_blank');
    } catch (error) {
      console.error('Error transcribing video:', error);
      alert('Error transcribing video. Please try again later.');
    }
  };

  const handleDownload = async (format) => {
    if (format === "csv") {
      let csvContent =
        "data:text/csv;charset=utf-8," +
        "First Name,Categories,Jira Issue,Today's Focus,Accomplishments,Blocker,Video URL,Posted Date,Status\n";

      filteredActivities.forEach((activity) => {
        const row = `${activity.firstName},"${activity.categories}","${activity.jira_issue
          }","${activity.today_focus || ""}","${activity.accomplishments || ""}","${activity.blockers || ""
          }","${moment(activity.createdAt).format("MM-DD-YYYY")}","${activity.loom_url || ""
          }","${activity.posted_to_client === 1 ? "Approved" : "Pending"}"\n`;
        csvContent += row;
      });

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "pod_activities.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (format === "pdf") {
      if (filteredActivities.length > 0) {
        const doc = new jsPDF('landscape');
        const headers = [
          "First Name", "Categories", "Jira Issue", "Today's Focus",
          "Accomplishments", "Blocker", "Video URL", "Posted Date", "Status"
        ];

        const rows = filteredActivities.map((activity) => [
          activity.firstName,
          activity.categories,
          activity.jira_issue,
          activity.today_focus || "",
          activity.accomplishments || "",
          activity.blockers || "",
          moment(activity.createdAt).format("MM-DD-YYYY"),
          activity.loom_url || "",
          activity.posted_to_client === 1 ? "Approved" : "Pending",
        ]);

        doc.autoTable({
          head: [headers],
          body: rows,
          styles: { fontSize: 8, cellPadding: 3 },
          headStyles: { fillColor: [0, 180, 216] },
          margin: { top: 10 },
        });

        doc.save("pod_activities.pdf");
      }
    }
    setDownloadMenuAnchor(null);
  };

  const rowsPerPage = 10;
  const totalPages = Math.ceil(filteredActivities.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedActivities = filteredActivities.slice(startIndex, endIndex);
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };


  return (
    <Box className="artemy-fade-up">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <div className="artemy-page-title">{intl.formatMessage({ id: 'podActivity' })}</div>
          <div className="artemy-page-sub">{intl.formatMessage({ id: 'viewAndManagePodActivities' }, { defaultMessage: 'View and manage all pod development activities' })}</div>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Breadcrumbs separator={<span style={{ color: 'var(--artemy-text-dim)' }}>›</span>} aria-label="breadcrumb">
            <Link 
              to="/control-plane"
              style={{
                color: 'var(--artemy-text-secondary)',
                textDecoration: 'none',
                fontSize: '13px',
                fontFamily: 'var(--artemy-font-mono)',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}
            >
              {intl.formatMessage({ id: 'dashboard' })}
            </Link>
            <Typography variant="body2" sx={{ 
              color: 'var(--artemy-accent)', 
              fontSize: '13px', 
              fontFamily: 'var(--artemy-font-mono)',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              {intl.formatMessage({ id: 'podActivity' })}
            </Typography>
          </Breadcrumbs>
        </Box>
      </Box>

      {isLoading ? (
        <LoaderWrapper>
          <CircularProgress size={40} sx={{ color: 'var(--artemy-accent)' }} />
          <Typography sx={{ color: 'var(--artemy-text-secondary)', fontFamily: 'var(--artemy-font-mono)', fontSize: 11, letterSpacing: 1 }}>
            {intl.formatMessage({ id: "loadingData" })}
          </Typography>
        </LoaderWrapper>
      ) : (
        <ContentWrapper>
          <>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {stats.map((stat, index) => (
                <Grid item xs={12} sm={4} key={index}>
                  <div className="artemy-card" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 10, color: 'var(--artemy-text-dim)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
                      {stat.title}
                    </div>
                    <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 28, fontWeight: 700, color: stat.color }}>
                      {stat.value}
                    </div>
                  </div>
                </Grid>
              ))}
            </Grid>

            <div className="artemy-card">
              <div style={{ padding: '20px', borderBottom: '1px solid var(--artemy-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel sx={{ color: 'var(--artemy-text-dim)', fontSize: 13 }}>{intl.formatMessage({ id: "selectPods" })}</InputLabel>
                    <Select
                      multiple
                      size="small"
                      value={selectedPodId}
                      onChange={handlePodSelect}
                      input={<OutlinedInput label={intl.formatMessage({ id: "selectPods" })} sx={{ 
                        background: 'var(--artemy-bg-elevated)',
                        '& fieldset': { borderColor: 'var(--artemy-border)' }
                      }} />}
                      renderValue={(selected) => {
                        if (selected.length === 0) return intl.formatMessage({ id: "allPods" });
                        return podDetails.filter((p) => selected.includes(p.id)).map((p) => p.podName).join(", ");
                      }}
                      sx={{ color: 'var(--artemy-text-primary)', fontSize: 13 }}
                    >
                      {podDetails.map((pod) => (
                        <MenuItem key={pod.id} value={pod.id}>
                          <Checkbox checked={selectedPodId.indexOf(pod.id) > -1} size="small" sx={{ color: 'var(--artemy-text-dim)' }} />
                          <ListItemText primary={pod.podName} primaryTypographyProps={{ fontSize: 13 }} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <button className="artemy-btn artemy-btn-ghost" onClick={() => setShowFilters(!showFilters)}>
                    <FilterListIcon sx={{ fontSize: 18 }} />
                    {intl.formatMessage({ id: "filters" })}
                  </button>
                  
                  <button className="artemy-btn artemy-btn-ghost" onClick={() => setShowDateRangePicker(!showDateRangePicker)}>
                    <DateRangeIcon sx={{ fontSize: 18 }} />
                    {intl.formatMessage({ id: "date" })}
                  </button>
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                  {selectedIds.length > 0 && (
                    <button className="artemy-btn artemy-btn-primary" style={{ background: 'var(--artemy-green)' }} onClick={handleApprove}>
                      {intl.formatMessage({ id: "approve" })} ({selectedIds.length})
                    </button>
                  )}
                  <button className="artemy-btn artemy-btn-primary" onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}>
                    <DownloadIcon sx={{ fontSize: 18 }} />
                    {intl.formatMessage({ id: "export" })}
                  </button>
                </div>
              </div>
            </div>

            {/* Dialogs and Menus */}
            <Menu
              anchorEl={downloadMenuAnchor}
              open={Boolean(downloadMenuAnchor)}
              onClose={() => setDownloadMenuAnchor(null)}
            >
              <MenuItem onClick={() => handleDownload("csv")}>
                <ListItemIcon>
                  <TableRowsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>CSV</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleDownload("pdf")}>
                <ListItemIcon>
                  <PictureAsPdfIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>PDF</ListItemText>
              </MenuItem>
            </Menu>

              <Dialog
                open={showDateRangePicker}
                onClose={() => setShowDateRangePicker(false)}
                maxWidth="md"
              >
                <DialogTitle sx={{ bgcolor: 'var(--artemy-bg-elevated)', color: 'var(--artemy-text-primary)' }}>{intl.formatMessage({ id: "selectDateRange" })}</DialogTitle>
                <DialogContent sx={{ bgcolor: 'var(--artemy-bg-elevated)', p: 0 }}>
                  <DateRangePicker
                    onChange={item => setTempDateRange([item.selection])}
                    ranges={tempDateRange}
                    months={window.innerWidth > 900 ? 2 : 1}
                    direction="horizontal"
                  />
                  <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 2, bgcolor: 'var(--artemy-bg-elevated)', borderTop: '1px solid var(--artemy-border)' }}>
                    <button className="artemy-btn artemy-btn-ghost" onClick={() => setShowDateRangePicker(false)}>
                      Cancel
                    </button>
                    <button 
                      className="artemy-btn artemy-btn-ghost" 
                      style={{ color: 'var(--artemy-red)' }}
                      onClick={() => {
                        setTempDateRange([{
                          startDate: null,
                          endDate: null,
                          key: "selection"
                        }]);
                        setDateRange([{
                          startDate: null,
                          endDate: null,
                          key: "selection"
                        }]);
                        setShowDateRangePicker(false);
                      }}
                    >
                      Clear Filter
                    </button>
                    <button 
                      className="artemy-btn artemy-btn-primary" 
                      onClick={() => {
                        setDateRange(tempDateRange);
                        setShowDateRangePicker(false);
                      }}
                    >
                      Apply Range
                    </button>
                  </Box>
                </DialogContent>
              </Dialog>

            <Drawer
              anchor="right"
              open={showFilters}
              onClose={() => setShowFilters(false)}
            >
              <Box sx={{ width: 300, p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {intl.formatMessage({ id: "filters" })}
                </Typography>
                <Stack spacing={3}>
                  <FormControl fullWidth>
                    <InputLabel>{intl.formatMessage({ id: "teamMember" })}</InputLabel>
                    <Select
                      value={selectedTeamMember}
                      onChange={handleTeamMemberSelect}
                      input={<OutlinedInput label={intl.formatMessage({ id: "teamMember" })} />}
                    >
                      <MenuItem value="">
                        {intl.formatMessage({ id: "allMembers" })}
                      </MenuItem>
                      {[...new Set(podActivities.map((activity) => activity.firstName))].map(
                        (member) => (
                          <MenuItem key={member} value={member}>
                            {member}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>{intl.formatMessage({ id: "status" })}</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={handleStatusFilterChange}
                      input={<OutlinedInput label={intl.formatMessage({ id: "status" })} />}
                    >
                      <MenuItem value="all">
                        {intl.formatMessage({ id: "allStatus" })}
                      </MenuItem>
                      <MenuItem value="pending">
                        {intl.formatMessage({ id: "pending" })}
                      </MenuItem>
                      <MenuItem value="approved">
                        {intl.formatMessage({ id: "approved" })}
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {/* Optional: Add clear filters button */}
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setStatusFilter("all");
                      setSelectedTeamMember("");
                      setSelectedPodId([]);
                      setDateRange([{
                        startDate: null,
                        endDate: null,
                        key: "selection"
                      }]);
                      setCurrentPage(1);
                    }}
                    fullWidth
                  >
                    {intl.formatMessage({ id: "clearFilters" })}
                  </Button>
                </Stack>
              </Box>
            </Drawer>

            {/* Main Table */}
            <div className="artemy-card" style={{ padding: '0', overflow: 'hidden', marginTop: '24px' }}>
              <div style={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr>
                      <th style={{ backgroundColor: 'var(--artemy-bg-base)', borderBottom: '1px solid var(--artemy-border)', padding: '16px', position: 'sticky', top: 0, zIndex: 1 }}>
                        <Checkbox
                          color="primary"
                          sx={{ color: 'var(--artemy-text-secondary)' }}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const allIds = paginatedActivities
                                .filter((activity) => activity.posted_to_client !== 1)
                                .map((activity) => activity.id);
                              setSelectedIds(allIds);
                            } else {
                              setSelectedIds([]);
                            }
                          }}
                        />
                      </th>
                      <th style={{ backgroundColor: 'var(--artemy-bg-base)', color: 'var(--artemy-text-secondary)', fontFamily: 'var(--artemy-font-mono)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--artemy-border)', padding: '16px', position: 'sticky', top: 0, zIndex: 1 }}>{intl.formatMessage({ id: "teamMember" })}</th>
                      <th style={{ backgroundColor: 'var(--artemy-bg-base)', color: 'var(--artemy-text-secondary)', fontFamily: 'var(--artemy-font-mono)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--artemy-border)', padding: '16px', position: 'sticky', top: 0, zIndex: 1 }}>{intl.formatMessage({ id: "jiraIssue" })}</th>
                      <th style={{ backgroundColor: 'var(--artemy-bg-base)', color: 'var(--artemy-text-secondary)', fontFamily: 'var(--artemy-font-mono)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--artemy-border)', padding: '16px', position: 'sticky', top: 0, zIndex: 1 }}>{intl.formatMessage({ id: "todaysFocus" })}</th>
                      <th style={{ backgroundColor: 'var(--artemy-bg-base)', color: 'var(--artemy-text-secondary)', fontFamily: 'var(--artemy-font-mono)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--artemy-border)', padding: '16px', position: 'sticky', top: 0, zIndex: 1 }}>{intl.formatMessage({ id: "accomplishments" })}</th>
                      <th style={{ backgroundColor: 'var(--artemy-bg-base)', color: 'var(--artemy-text-secondary)', fontFamily: 'var(--artemy-font-mono)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--artemy-border)', padding: '16px', position: 'sticky', top: 0, zIndex: 1 }}>{intl.formatMessage({ id: "blocker" })}</th>
                      <th style={{ backgroundColor: 'var(--artemy-bg-base)', color: 'var(--artemy-text-secondary)', fontFamily: 'var(--artemy-font-mono)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--artemy-border)', padding: '16px', position: 'sticky', top: 0, zIndex: 1 }}>{intl.formatMessage({ id: "videoUrl" })}</th>
                      <SortableTableHeader
                        column="createdAt"
                        label={intl.formatMessage({ id: "postedDate" })}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        column="status"
                        label={intl.formatMessage({ id: "status" })}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                      <th style={{ backgroundColor: 'var(--artemy-bg-base)', color: 'var(--artemy-text-secondary)', fontFamily: 'var(--artemy-font-mono)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--artemy-border)', padding: '16px', position: 'sticky', top: 0, zIndex: 1 }}>{intl.formatMessage({ id: "actions" })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedActivities.length > 0 ? (
                      paginatedActivities.map((activity) => (
                        <EditableTableRow
                          key={activity.id}
                          activity={activity}
                          userStories={userStories}
                          selectedIds={selectedIds}
                          handleCheckboxChange={handleCheckboxChange}
                          handleViewAndTranscribe={handleViewAndTranscribe}
                          onSave={() => {
                            fetchData();
                          }}
                          token={token}
                          user={user}
                          intl={intl}
                        />
                      ))
                    ) : (
                      <tr>
                        <td colSpan={10} style={{ textAlign: 'center', padding: '80px 0' }}>
                          <Box sx={{ color: 'var(--artemy-text-secondary)', opacity: 0.5 }}>
                            <TableRowsIcon sx={{ fontSize: 48, mb: 2 }} />
                            <Typography variant="body1">No activities found for the selected criteria</Typography>
                          </Box>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(e, page) => setCurrentPage(page)}
                sx={{ 
                  '& .MuiPaginationItem-root': { color: 'var(--artemy-text-secondary)' },
                  '& .Mui-selected': { background: 'rgba(61, 127, 255, 0.12) !important', color: 'var(--artemy-accent) !important' }
                }}
                shape="rounded"
              />
            </Box>
          </>
        </ContentWrapper>
      )}
    </Box>
  );
}

export default PodActivitystakeholderviewall;